/**
 * Unscheduled company-investigation runs and their stored LLM steps.
 * listRuns already drops rows with a scheduleId. This module drops them again.
 */

export const INVESTIGATION_STEP_ROLES = [
  'identity_public',
  'product_catalog',
  'product_regulation',
  'building_sites',
  'org_structure',
  'people_officers',
  'people_ubo',
  'people_events',
  'registry_competitors',
  'financial_reports',
  'merge_patch',
] as const;

export type ContinueBlock = 'allowed' | 'not_latest' | 'not_failed' | 'run_busy';

const BUSY_RUN_STATUSES = new Set(['pending', 'in_progress']);

export interface IWatchInvestigationRun {
  id: number,
  status: string,
  modelUsed: string | null,
  startedAt: string | null,
  finishedAt: string | null,
  createdAt: string | null,
  costUsd: string | number | null,
  outputMessage: string | null,
  scheduleId: number | null,
  watchCompanyId: number | null,
}

export interface IWatchInvestigationLlmStep {
  id: number,
  agentRunId: number,
  stepIndex: number,
  promptRole: string,
  subAgentKind: string | null,
  status: string,
  modelUsed: string | null,
  searchCallCount: number | null,
  costUsd: string | number | null,
  responseJson: unknown,
  startedAt: string | null,
  finishedAt: string | null,
  errorMessage: string | null,
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function asPositiveId(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return null;
  }
  return num;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const text = value.trim();
  return text || null;
}

function asCost(value: unknown): string | number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  return null;
}

function asCount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }
  return null;
}

function outputMessage(value: unknown): string | null {
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) {
      return null;
    }
    try {
      return outputMessage(JSON.parse(text));
    } catch {
      return text;
    }
  }
  if (!isRecord(value)) {
    return null;
  }
  return asText(value.message);
}

function promptRoleFor(row: Record<string, unknown>, stepIndex: number): string {
  const direct = asText(row.promptRole);
  if (direct) {
    return direct;
  }
  if (isRecord(row.requestJson)) {
    const nested = asText(row.requestJson.promptRole);
    if (nested) {
      return nested;
    }
  }
  return INVESTIGATION_STEP_ROLES[stepIndex - 1] || `step_${stepIndex}`;
}

export function parseInvestigationRuns(body: unknown): IWatchInvestigationRun[] {
  if (!Array.isArray(body)) {
    return [];
  }
  const runs: IWatchInvestigationRun[] = [];
  body.forEach((item) => {
    if (!isRecord(item)) {
      return;
    }
    const id = asPositiveId(item.id);
    const status = asText(item.status)?.toLowerCase();
    if (!id || !status) {
      return;
    }
    runs.push({
      id,
      status,
      modelUsed: asText(item.modelUsed) || asText(item.model) || asText(item.modelId),
      startedAt: asText(item.startedAt),
      finishedAt: asText(item.finishedAt),
      createdAt: asText(item.createdAt),
      costUsd: asCost(item.costUsd),
      outputMessage: outputMessage(item.output),
      scheduleId: asPositiveId(item.scheduleId),
      watchCompanyId: asPositiveId(item.watchCompanyId),
    });
  });
  return runs
    .filter((run) => run.scheduleId == null)
    .sort((left, right) => right.id - left.id);
}

export function parseInvestigationSteps(body: unknown): IWatchInvestigationLlmStep[] {
  if (!Array.isArray(body)) {
    return [];
  }
  const steps: IWatchInvestigationLlmStep[] = [];
  body.forEach((item) => {
    if (!isRecord(item)) {
      return;
    }
    const id = asPositiveId(item.id);
    const stepIndex = asCount(item.stepIndex);
    if (!id || stepIndex == null) {
      return;
    }
    steps.push({
      id,
      agentRunId: asPositiveId(item.agentRunId) || 0,
      stepIndex,
      promptRole: promptRoleFor(item, stepIndex),
      subAgentKind: asText(item.subAgentKind),
      status: asText(item.status)?.toLowerCase() || 'unknown',
      modelUsed: asText(item.modelUsed) || asText(item.model) || asText(item.modelId),
      searchCallCount: asCount(item.searchCallCount),
      costUsd: asCost(item.costUsd),
      responseJson: item.responseJson ?? null,
      startedAt: asText(item.startedAt),
      finishedAt: asText(item.finishedAt),
      errorMessage: asText(item.errorMessage),
    });
  });
  return steps.sort((left, right) => left.stepIndex - right.stepIndex);
}

export function latestInvestigationRun(
  runs: IWatchInvestigationRun[],
): IWatchInvestigationRun | null {
  return runs.reduce<IWatchInvestigationRun | null>((best, run) => {
    if (!best || run.id > best.id) {
      return run;
    }
    return best;
  }, null);
}

/**
 * Continue is the latest unscheduled run, and only when that run failed.
 * Any pending or in_progress unscheduled run blocks Continue.
 */
export function continueBlock(
  runId: number,
  runs: IWatchInvestigationRun[],
): ContinueBlock {
  const latest = latestInvestigationRun(runs);
  if (!latest || runId !== latest.id) {
    return 'not_latest';
  }
  if (runs.some((run) => BUSY_RUN_STATUSES.has(run.status))) {
    return 'run_busy';
  }
  if (latest.status !== 'failed') {
    return 'not_failed';
  }
  return 'allowed';
}

export function runHasMergePatch(steps: IWatchInvestigationLlmStep[]): boolean {
  return steps.some((step) => step.promptRole === 'merge_patch' && step.status === 'succeeded');
}

export function formatInvestigationStamp(value: string | null): string {
  if (!value) {
    return '—';
  }
  return value.replace('T', ' ').replace('Z', '').slice(0, 16);
}

export function formatInvestigationCost(value: string | number | null): string {
  if (value == null || value === '') {
    return '—';
  }
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) {
    return String(value);
  }
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

export function investigationTime(run: {
  startedAt: string | null,
  finishedAt: string | null,
  createdAt?: string | null,
}): string {
  return formatInvestigationStamp(run.finishedAt || run.startedAt || run.createdAt || null);
}

export function watchRequestErrorText(error: unknown): string | null {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return null;
  }
  const { data } = error as { data?: unknown };
  if (!isRecord(data) || !('message' in data)) {
    return null;
  }
  const { message } = data;
  const text = asText(message);
  if (text) {
    return text;
  }
  if (!Array.isArray(message)) {
    return null;
  }
  const first = message.find((item) => asText(item));
  return asText(first);
}
